/* eslint-disable react-hooks/exhaustive-deps */
import { Search, RotateCw } from 'lucide-react'
import moment from 'moment'
import { useEffect, useState } from 'react'
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Row,
  Table
} from 'react-bootstrap'
import { Loader } from 'react-bootstrap-typeahead'
import { useLocation, useNavigate } from 'react-router-dom'
import Select from 'react-select'
import RowEmpty from '../../../components/atoms/RowEmpty'
import TableAction from '../../../components/atoms/Table/TableAction'
import TablePagination from '../../../components/atoms/Table/TablePagination'
import InputDatePicker from '../../../components/Input/DatePicker/InputDatePicker'
import api from '../../../config/AxiosInstance'
import { convertDateWithoutTime } from '../../../helpers/constants'
import * as Common from '../../../utils/Common'

const headerTitleStyle = {
  color: 'var(--primary-orange)',
  fontSize: '24px',
  fontWeight: 600
}

const tableHeaderStyle = {
  fontSize: '14px',
  fontWeight: '600',
  color: '#333333',
  letterSpacing: '0.5px',
  backgroundColor: '#f8f9fa',
  borderBottom: '2px solid #e9ecef'
}

const cardFilterStyle = {
  border: 'none',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  borderRadius: '12px',
  padding: '1.5rem',
  marginBottom: '1.5rem',
  backgroundColor: '#fff'
}

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: '700',
  color: '#333333',
  textTransform: 'uppercase',
  marginBottom: '0.5rem',
  letterSpacing: '0.5px'
}

const SelectStyles = {
  control: (base, state) => ({
    ...base,
    height: 45,
    minHeight: 45,
    borderRadius: '10px',
    borderColor: state.isFocused ? 'var(--primary-orange)' : '#e9ecef',
    boxShadow: state.isFocused ? '0 0 0 4px rgba(248, 157, 29, 0.1)' : 'none',
    backgroundColor: '#ffffff',
    border: '1px solid #e9ecef',
    fontSize: '0.9rem'
  }),
  valueContainer: (provided) => ({
    ...provided,
    height: '45px',
    padding: '0 12px'
  }),
  input: (provided) => ({
    ...provided,
    margin: '0px'
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#495057'
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? 'var(--primary-orange)'
      : state.isFocused
        ? '#fff5e6'
        : null,
    color: state.isSelected ? 'white' : '#333',
    cursor: 'pointer'
  })
}

const FormControlStyle = {
  height: '45px',
  borderRadius: '10px',
  border: '1px solid #e9ecef',
  backgroundColor: '#ffffff',
  fontSize: '0.9rem'
}

const DeliveryHistory = () => {
  const { state } = useLocation()
  const navigate = useNavigate()

  const [deliveryList, setDeliveryList] = useState([])
  const [loading, setLoading] = useState(false)
  const [transportVehicles, setTransportVehicles] = useState([])
  const [warehouses, setWarehouses] = useState([])
  const [filter, setFilter] = useState({
    plate_no: state?.plate_no || '',
    search: '',
    warehouse_id: state?.warehouse_id || '',
    startDate: state?.startDate
      ? moment(state.startDate).format('YYYY-MM-DD')
      : '',
    endDate: state?.endDate ? moment(state.endDate).format('YYYY-MM-DD') : ''
  })
  const [debouncedSearch, setDebouncedSearch] = useState('')

  const [meta, setMeta] = useState({
    per_page: 10,
    current_page: 1,
    total_row: 0,
    total_page: 0
  })

  const fetchData = async () => {
    try {
      setLoading(true)
      const { data } = await api.get('outbound/delivery_history/list', {
        params: {
          page: meta.current_page,
          per_page: meta.per_page,
          plate_no: filter.plate_no,
          q: debouncedSearch,
          warehouse_id: filter.warehouse_id,
          start_delivery_date: filter.startDate,
          end_delivery_date: filter.endDate
        }
      })
      if (data.success) {
        setDeliveryList(data.data)
        setMeta(data.metadata)
      } else {
        setDeliveryList([])
      }
    } catch (error) {
      if (error?.response?.status !== 404) {
        Common.showPopUpErrorMessage(
          error.response?.data?.errors?.message || 'An error occurred'
        )
      } else {
        setDeliveryList([])
      }
    } finally {
      setLoading(false)
    }
  }

  const fetchPlateNo = async () => {
    try {
      const { data } = await api.get('master/vehicles/all')
      if (data.success) {
        setTransportVehicles(data.data)
      }
    } catch (error) {
      if (error?.response?.status !== 404) {
        Common.showPopUpErrorMessage(
          error.response?.data?.errors?.message || 'An error occurred'
        )
      }
    }
  }

  const fetchWarehouses = async () => {
    try {
      const params = {
        is_parent: 1,
        forUser: 1
      }
      const response = await api.get('/master/warehouses/all', { params })
      const { data } = response.data
      setWarehouses(data)
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.message || 'An error occurred'
      Common.showPopUpErrorMessage(errMsg)
    }
  }

  const handleFilterChange = (name, value) => {
    setFilter((prev) => ({ ...prev, [name]: value }))
    setMeta((prev) => ({ ...prev, current_page: 1 }))
  }

  const handleDateChange = (name, date) => {
    if (date) {
      setFilter((prev) => ({
        ...prev,
        [name]: moment(date).format('YYYY-MM-DD')
      }))
    } else {
      setFilter((prev) => ({
        ...prev,
        [name]: ''
      }))
    }
    setMeta((prev) => ({ ...prev, current_page: 1 }))
  }

  const handlePageClick = ({ selected }) => {
    setMeta((prev) => ({ ...prev, current_page: selected + 1 }))
    window.scrollTo(0, 0)
  }

  const handleMetaChange = (e) => {
    setMeta((prev) => ({
      ...prev,
      per_page: parseInt(e.target.value, 10),
      current_page: 1
    }))
  }

  const navigateToDetail = ({ outbound_id }) => {
    navigate(
      `/outbound/delivery-history/detail/${encodeURIComponent(outbound_id)}`,
      {
        state: filter
      }
    )
  }

  const handleReset = () => {
    setFilter({
      plate_no: '',
      search: '',
      warehouse_id: '',
      startDate: '',
      endDate: ''
    })
    setDebouncedSearch('')
    setMeta((prev) => ({ ...prev, current_page: 1 }))
  }

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(filter.search)
      setMeta((prev) => ({ ...prev, current_page: 1 }))
    }, 1000)
    return () => clearTimeout(handler)
  }, [filter.search])

  useEffect(() => {
    const isDateValid =
      (!filter.startDate && !filter.endDate) ||
      (filter.startDate && filter.endDate)

    if (isDateValid) {
      fetchData()
    }
  }, [
    meta.per_page,
    meta.current_page,
    meta.current_page,
    filter.plate_no,
    debouncedSearch,
    filter.warehouse_id,
    filter.startDate,
    filter.endDate
  ])

  useEffect(() => {
    fetchPlateNo()
    fetchWarehouses()
  }, [])

  const warehouseOptions = [
    { value: '', label: 'All Warehouses' },
    ...warehouses.map((w) => ({ value: w.id, label: w.warehouse_name }))
  ]

  const vehicleOptions = [
    { value: '', label: 'All Plate No' },
    ...transportVehicles.map((v) => ({ value: v.plate_no, label: v.plate_no }))
  ]

  return (
    <Container fluid className="px-4">
      <Row className="mb-4 align-items-center">
        <Col>
          <h3 style={headerTitleStyle}>Delivery History</h3>
        </Col>
        <Col className="text-end">
          <Button
            variant="light"
            className="text-secondary fw-bold rounded px-3 border"
            onClick={handleReset}
            style={{ fontSize: '0.85rem', height: '37px' }}
          >
            <RotateCw size={16} className="me-2" />
            Reset Filters
          </Button>
        </Col>
      </Row>

      <Card style={cardFilterStyle}>
        <Row className="g-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label style={labelStyle}>Search</Form.Label>
              <InputGroup>
                <InputGroup.Text
                  style={{
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    borderRight: 'none',
                    borderTopLeftRadius: '10px',
                    borderBottomLeftRadius: '10px'
                  }}
                >
                  <Search size={18} className="text-muted" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search driver or ticket outbound"
                  value={filter.search}
                  onChange={(e) =>
                    setFilter((prev) => ({ ...prev, search: e.target.value }))
                  }
                  style={{
                    ...FormControlStyle,
                    borderLeft: 'none',
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0
                  }}
                />
              </InputGroup>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label style={labelStyle}>Warehouse</Form.Label>
              <Select
                value={warehouseOptions.find(
                  (o) => o.value === filter.warehouse_id
                )}
                onChange={(e) =>
                  handleFilterChange('warehouse_id', e?.value || '')
                }
                options={warehouseOptions}
                styles={SelectStyles}
                placeholder="All Warehouses"
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label style={labelStyle}>Plate No</Form.Label>
              <Select
                name="plate_no"
                value={vehicleOptions.find((o) => o.value === filter.plate_no)}
                onChange={(opt) =>
                  handleFilterChange('plate_no', opt ? opt.value : '')
                }
                options={vehicleOptions}
                styles={SelectStyles}
                placeholder="All Plate No"
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label style={labelStyle}>Start Date</Form.Label>
              <div
                style={FormControlStyle}
                className="d-flex align-items-center px-2"
              >
                <InputDatePicker
                  value={filter.startDate ? new Date(filter.startDate) : null}
                  onChange={(date) => handleDateChange('startDate', date)}
                  clearIcon
                />
              </div>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label style={labelStyle}>End Date</Form.Label>
              <div
                style={FormControlStyle}
                className="d-flex align-items-center px-2"
              >
                <InputDatePicker
                  value={filter.endDate ? new Date(filter.endDate) : null}
                  onChange={(date) => handleDateChange('endDate', date)}
                  clearIcon
                />
              </div>
            </Form.Group>
          </Col>
        </Row>
      </Card>

      <Row>
        <Col md={12}>
          <Table
            hover
            responsive
            className="shadow-sm mb-5 bg-white"
            style={{
              borderRadius: '12px',
              overflow: 'hidden',
              fontSize: '0.9rem'
            }}
          >
            <thead style={{ backgroundColor: '#f8f9fa' }}>
              <tr>
                <th className="p-2 align-middle" style={tableHeaderStyle}>
                  Plate No.
                </th>
                <th className="p-2 align-middle" style={tableHeaderStyle}>
                  Warehouse
                </th>
                <th className="p-2 align-middle" style={tableHeaderStyle}>
                  Vehicle Type
                </th>
                <th className="p-2 align-middle" style={tableHeaderStyle}>
                  Driver
                </th>
                <th className="p-2 align-middle" style={tableHeaderStyle}>
                  Capacity(Kg)
                </th>
                <th className="p-2 align-middle" style={tableHeaderStyle}>
                  Total qty Kg/ Outbound Plan
                </th>
                <th className="p-2 align-middle" style={tableHeaderStyle}>
                  Total SJ In Outbound Plan
                </th>
                <th className="p-2 align-middle" style={tableHeaderStyle}>
                  #Outbound
                </th>
                <th className="p-2 align-middle" style={tableHeaderStyle}>
                  Delivery Date
                </th>
                <th className="p-2 align-middle" style={tableHeaderStyle}>
                  Delivery Status
                </th>
                <th
                  className="p-2 align-middle text-end"
                  style={tableHeaderStyle}
                >
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={11} className="text-center p-5">
                    <Loader />
                  </td>
                </tr>
              ) : !deliveryList.length ? (
                <RowEmpty colSpan={11} />
              ) : (
                deliveryList.map((item) => (
                  <tr key={item.outbound_id}>
                    <td className="p-2 align-middle">{item.plate_no}</td>
                    <td className="p-2 align-middle">
                      {item?.warehouse_name || '-'}
                    </td>
                    <td className="p-2 align-middle">{item.vehicle_type}</td>
                    <td className="p-2 align-middle">{item.driver}</td>
                    <td className="p-2 align-middle">{item.capacity}</td>
                    <td className="p-2 align-middle">
                      {item.total_qty_outbound}
                    </td>
                    <td className="p-2 align-middle">{item.total_sj}</td>
                    <td className="p-2 align-middle">
                      <a
                        className="fw-bold text-decoration-none"
                        href={`/outbound/plan/detail/${item.outbound_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: 'var(--primary-orange)' }}
                      >
                        {item.outbound || '-'}
                      </a>
                    </td>
                    <td className="p-2 align-middle">
                      {item.delivery_date
                        ? convertDateWithoutTime(item.delivery_date)
                        : '-'}
                    </td>
                    <td className="p-2 align-middle">
                      <Badge
                        pill
                        bg={
                          item.delivery_status === 'done'
                            ? 'success'
                            : 'primary'
                        }
                        className="text-uppercase"
                      >
                        {item.delivery_status}
                      </Badge>
                    </td>
                    <td className="p-2 align-middle text-end">
                      <TableAction>
                        <button
                          className="btn btn-light text-secondary border rounded shadow-sm"
                          style={{
                            width: '35px',
                            height: '35px',
                            padding: 0
                          }}
                          onClick={() => navigateToDetail(item)}
                        >
                          <Search size={16} />
                        </button>
                      </TableAction>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={11} className="p-3 border-top-0">
                  <TablePagination
                    meta={meta}
                    handleOnChangeMeta={handleMetaChange}
                    handlePageChange={handlePageClick}
                  />
                </td>
              </tr>
            </tfoot>
          </Table>
        </Col>
      </Row>
    </Container>
  )
}

export default DeliveryHistory
